/**
 * 03 · Development profile — the person's own result, for free, before any ask.
 *
 * Every number here is theirs: the index is the direction they just took, the
 * patterns are the ones their answers scored, and the "what it costs you" and
 * "how it would feel instead" lists are that pattern's own copy from the
 * content payload. Nothing on this screen is illustrative.
 *
 * Colour follows the app's zone scale, engine inversion included — a weak
 * engine reads red and a weak blocker reads green, in both directions.
 */
import React from 'react';
import { Icon } from '../app/icons';
import { c, hair, f, ACT, SCORE_ZONES, scoreColor, patternZone, effectiveStrength, DIR_ORDER } from '../app/tokens';
import { Body, Col, Meta, Muted, Row, SegBarC, Appear } from '../app/primitives';
import { Orb } from '../app/orb';
import { useTt } from '../i18n/useT';
import { PaywallPattern, PaywallProfile } from './data';
import { Card, Carousel, Cta, OK, Screen, StickyActions, TopBar, useWide } from './ui';

/** Which state line to show, by the index's zone. Same order as `SCORE_ZONES`. */
const STATE_KEYS = ['critical', 'fair', 'good', 'excellent'];

const zoneIndex = (score: number): number => {
    const i = SCORE_ZONES.findIndex((z) => score < z.max);

    return i < 0 ? SCORE_ZONES.length - 1 : i;
};

/** The four zones as a proportional ladder, with the current one raised. */
const ZoneScale: React.FC<{ score: number; wide?: boolean }> = ({ score, wide }) => {
    const { t } = useTt();
    const active = zoneIndex(score);
    const bands = SCORE_ZONES.map((z, i) => ({
        key: z.key,
        hex: z.hex,
        lo: i ? SCORE_ZONES[i - 1].max : 0,
        hi: Math.min(z.max - 1, 100),
    }));
    const cur = bands[active];

    return (
        <Col gap={10}>
            <Row style={{ gap: 5, alignItems: 'center', height: 10 }}>
                {bands.map((b, i) => (
                    <div
                        key={b.key}
                        style={{
                            // Width proportional to the band, so the ladder is a
                            // scale rather than four equal decorative blocks.
                            flex: b.hi - b.lo,
                            height: i === active ? 8 : 5,
                            borderRadius: 4,
                            background: i === active ? b.hex : `${b.hex}33`,
                            boxShadow: i === active ? `0 0 10px ${b.hex}66` : 'none',
                            transition: 'height 300ms',
                        }}
                    />
                ))}
            </Row>
            <Row style={{ justifyContent: 'space-between' }}>
                <Meta style={{ fontSize: wide ? 10 : 10.5, color: cur.hex, letterSpacing: '0.14em' }}>
                    {t(cur.key)}
                </Meta>
                <Meta style={{ fontSize: wide ? 10 : 10.5, color: c.muted, letterSpacing: '0.1em' }}>
                    {cur.lo}–{cur.hi}
                </Meta>
            </Row>
        </Col>
    );
};

/** Eight-segment donut with the taken direction lit — "1 of 8", drawn. */
const DirRing: React.FC<{ active: string; size: number }> = ({ active, size }) => {
    const { t } = useTt();
    const half = size / 2;
    const outer = size * 0.407;
    const inner = size * 0.285;
    const rad = (deg: number) => (deg * Math.PI) / 180;
    const point = (r: number, a: number): [number, number] => [
        half + r * Math.cos(rad(a)),
        half + r * Math.sin(rad(a)),
    ];

    return (
        <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
                {DIR_ORDER.map((code, i) => {
                    const a0 = -90 + i * 45 + 2.6;
                    const a1 = -90 + (i + 1) * 45 - 2.6;
                    const [x0, y0] = point(outer, a0);
                    const [x1, y1] = point(outer, a1);
                    const [x2, y2] = point(inner, a1);
                    const [x3, y3] = point(inner, a0);

                    return (
                        <path
                            key={code}
                            d={`M ${x0} ${y0} A ${outer} ${outer} 0 0 1 ${x1} ${y1} L ${x2} ${y2} A ${inner} ${inner} 0 0 0 ${x3} ${y3} Z`}
                            fill={code === active ? ACT : 'rgba(232,230,224,0.09)'}
                        />
                    );
                })}
            </svg>
            <Col style={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center' }} gap={2}>
                <span
                    style={{
                        fontFamily: f.mono,
                        fontSize: size * 0.245,
                        fontWeight: 300,
                        color: c.textPri,
                        letterSpacing: '-0.03em',
                    }}
                >
                    1
                </span>
                <Meta style={{ fontSize: 9, color: c.muted, letterSpacing: '0.14em' }}>{t('pw.pf.ofEight')}</Meta>
            </Col>
        </div>
    );
};

const PatternCard: React.FC<{ pattern: PaywallPattern; lead?: boolean; segments?: number }> = ({
    pattern,
    lead,
    segments = 10,
}) => {
    const z = patternZone(pattern.pct, pattern.pol);

    return (
        <Col
            style={{
                width: '100%',
                padding: '15px 16px 14px',
                borderRadius: 16,
                background: lead ? c.card : c.cardIn,
                border: lead ? `1px solid ${z.hex}30` : hair,
            }}
            gap={0}
        >
            <span
                style={{
                    fontSize: 15.5,
                    color: c.textPri,
                    fontWeight: 400,
                    letterSpacing: '-0.018em',
                    lineHeight: 1.25,
                }}
            >
                {pattern.name}
            </span>
            <Muted style={{ fontSize: 12, marginTop: 8, flex: 1 }}>{pattern.sub}</Muted>
            <Row style={{ gap: 10, alignItems: 'center', marginTop: 14 }}>
                {/* Bar length uses the engine-inverted value, same as the colour,
                    so a weak engine cannot read long-and-green next to a verdict
                    that says the opposite. */}
                <SegBarC pct={effectiveStrength(pattern.pct, pattern.pol)} hex={z.hex} n={segments} />
                <span
                    style={{
                        fontFamily: f.mono,
                        fontSize: 13.5,
                        fontWeight: 300,
                        color: z.hex,
                        letterSpacing: '-0.02em',
                    }}
                >
                    {pattern.pct}
                </span>
            </Row>
        </Col>
    );
};

/** The blocker's day-to-day signals, numbered in its own zone colour. */
const Costs: React.FC<{ items: string[]; hex: string; wide?: boolean }> = ({ items, hex, wide }) => {
    const { t } = useTt();

    return (
        <>
            <Row style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Meta style={{ fontSize: wide ? 10 : 9.5, color: c.muted, letterSpacing: '0.16em' }}>
                    {t('pw.pf.costs')}
                </Meta>
                <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.12em' }}>
                    {t('pw.pf.signals', { n: items.length })}
                </Meta>
            </Row>
            <Col style={{ marginTop: 13 }} gap={8}>
                {items.map((line, i) => (
                    <Row
                        key={line}
                        style={{
                            gap: 12,
                            alignItems: 'center',
                            padding: '12px 14px',
                            background: c.cardIn,
                            border: hair,
                            borderRadius: 13,
                        }}
                    >
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 11,
                                color: hex,
                                letterSpacing: '0.02em',
                                width: 16,
                                flex: '0 0 auto',
                            }}
                        >
                            {String(i + 1).padStart(2, '0')}
                        </span>
                        <Body style={{ fontSize: 13, flex: 1 }}>{line}</Body>
                    </Row>
                ))}
            </Col>
        </>
    );
};

/**
 * What changes when the pattern weakens.
 *
 * All of it is shown. An earlier version blurred everything past the first line
 * behind a lock; the point of this screen is that the person leaves knowing
 * something true about themselves, and a teaser undercuts that for a few
 * percent of curiosity. The first result is lifted, the rest simply follow.
 */
const Results: React.FC<{ items: string[]; wide?: boolean }> = ({ items, wide }) => {
    const { t } = useTt();
    const [first, ...rest] = items;

    return (
        <>
            <Row style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Meta style={{ fontSize: wide ? 10 : 9.5, color: c.muted, letterSpacing: '0.16em' }}>
                    {t('pw.pf.results')}
                </Meta>
                <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.12em' }}>
                    {t('pw.pf.resultsCount', { n: items.length })}
                </Meta>
            </Row>
            {first && (
                <Row
                    style={{
                        gap: 12,
                        alignItems: 'flex-start',
                        marginTop: 13,
                        padding: '13px 15px',
                        background: `${OK}0d`,
                        border: `1px solid ${OK}33`,
                        borderRadius: 14,
                    }}
                >
                    <div
                        style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            flex: '0 0 auto',
                            background: `${OK}22`,
                            display: 'grid',
                            placeItems: 'center',
                            marginTop: 1,
                        }}
                    >
                        <Icon k="check" size={12} color={OK} />
                    </div>
                    <Body style={{ fontSize: 13, flex: 1 }}>{first}</Body>
                </Row>
            )}
            <Col style={{ marginTop: 8 }} gap={7}>
                {rest.map((line) => (
                    <Row
                        key={line}
                        style={{
                            gap: 12,
                            alignItems: 'center',
                            padding: '12px 15px',
                            background: c.cardIn,
                            border: hair,
                            borderRadius: 13,
                        }}
                    >
                        <div
                            style={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                flex: '0 0 auto',
                                background: `${OK}14`,
                                display: 'grid',
                                placeItems: 'center',
                            }}
                        >
                            <Icon k="check" size={11} color={OK} />
                        </div>
                        <Body style={{ fontSize: 13, flex: 1 }}>{line}</Body>
                    </Row>
                ))}
            </Col>
        </>
    );
};

const Insight: React.FC<{ blockerName: string; wide?: boolean; style?: React.CSSProperties }> = ({
    blockerName,
    wide,
    style,
}) => {
    const { t } = useTt();

    return (
        <Card
            accent={`${ACT}2e`}
            style={{
                background: `linear-gradient(150deg, ${ACT}0d, ${c.card} 58%)`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                ...style,
            }}
        >
            <Meta style={{ fontSize: wide ? 10 : 9.5, color: ACT, letterSpacing: '0.16em' }}>{t('pw.pf.insight')}</Meta>
            <div
                style={{
                    fontSize: wide ? 20 : 17,
                    color: c.textPri,
                    fontWeight: 400,
                    letterSpacing: '-0.022em',
                    lineHeight: 1.3,
                    marginTop: 13,
                }}
            >
                {t('pw.pf.insight.lead')}
            </div>
            <div
                style={{
                    fontSize: wide ? 20 : 17,
                    color: ACT,
                    fontWeight: 400,
                    letterSpacing: '-0.022em',
                    lineHeight: 1.3,
                    marginTop: 4,
                }}
            >
                {t('pw.pf.insight.body', { pattern: blockerName })}
            </div>
        </Card>
    );
};

const DirectionCard: React.FC<{ profile: PaywallProfile; wide?: boolean }> = ({ profile, wide }) => {
    const { t } = useTt();
    const index = profile.index as number;
    const { hex } = scoreColor(index);

    return (
        <Card style={wide ? { padding: '22px 24px' } : undefined}>
            <Meta style={{ fontSize: wide ? 10 : 9.5, color: c.muted, letterSpacing: '0.16em' }}>
                {t('pw.pf.dirLabel')}
            </Meta>
            <Row style={{ gap: wide ? 22 : 16, marginTop: wide ? 16 : 14, alignItems: 'center' }}>
                <Col style={{ flex: 1, minWidth: 0 }} gap={wide ? 11 : 9}>
                    <Row style={{ gap: wide ? 11 : 9, alignItems: 'center' }}>
                        <Icon k={profile.dirCode || 'tDirs'} size={wide ? 18 : 16} color={ACT} />
                        <span
                            style={{
                                fontSize: wide ? 24 : 21,
                                color: c.textPri,
                                fontWeight: 400,
                                letterSpacing: '-0.024em',
                            }}
                        >
                            {profile.dirName}
                        </span>
                    </Row>
                    <Muted style={{ fontSize: wide ? 13.5 : 12.5 }}>{profile.dirSub}</Muted>
                    <Row style={{ gap: wide ? 13 : 11, alignItems: 'center', marginTop: 2 }}>
                        <SegBarC pct={index} hex={hex} n={wide ? 12 : 10} />
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: wide ? 16 : 14,
                                fontWeight: 300,
                                color: hex,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {index}
                        </span>
                    </Row>
                </Col>
                <DirRing active={profile.dirCode || ''} size={wide ? 96 : 86} />
            </Row>
        </Card>
    );
};

interface Props {
    profile: PaywallProfile;
    onNext: () => void;
    onBack?: () => void;
}

const Profile: React.FC<Props> = ({ profile, onNext, onBack }) => {
    const { t } = useTt();
    const wide = useWide();

    // The screen is entirely derived from a measured index, so there is nothing
    // truthful to draw without one.
    if (!profile.ready || profile.index == null) {
        return (
            <Screen>
                <TopBar onBack={onBack} />
                <div style={{ padding: '80px 20px 40px' }}>
                    <Cta label={t('pw.continue')} onClick={onNext} />
                </div>
            </Screen>
        );
    }

    const { index, blocker } = profile;
    const { hex } = scoreColor(index);
    const state = STATE_KEYS[zoneIndex(index)];
    const shown = profile.patterns.slice(0, 3);
    const bz = blocker ? patternZone(blocker.pct, blocker.pol) : null;

    const stateLine = (
        <div>
            <div style={{ fontSize: wide ? 16 : 16.5, color: hex, fontWeight: 400, letterSpacing: '-0.018em' }}>
                {t(`pw.pf.state.${state}.title`)}
            </div>
            <Muted style={{ fontSize: 13, marginTop: 9 }}>{t(`pw.pf.state.${state}.body`)}</Muted>
        </div>
    );

    const patternsHead = (
        <Row style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Meta style={{ fontSize: wide ? 10 : 9.5, color: c.muted, letterSpacing: '0.16em' }}>
                {t('pw.pf.patterns')}
            </Meta>
            <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.12em' }}>
                {t('pw.pf.patternsCount', { n: shown.length, total: profile.patternTotal })}
            </Meta>
        </Row>
    );

    if (wide) {
        return (
            <Screen wide style={{ padding: '38px 40px 56px' }}>
                <Row style={{ gap: 30, alignItems: 'stretch' }}>
                    <Appear style={{ width: 410, flex: '0 0 auto', display: 'flex' }}>
                        <Col style={{ flex: 1 }} gap={18}>
                            <Card style={{ padding: '26px 24px 22px' }}>
                                <Orb score={index} size={210} label={t('pw.pf.index')} />
                                <div style={{ marginTop: 20 }}>
                                    <ZoneScale score={index} wide />
                                </div>
                                <div style={{ marginTop: 20, paddingTop: 18, borderTop: hair }}>{stateLine}</div>
                            </Card>
                            {blocker && <Insight blockerName={blocker.name} wide style={{ flex: 1 }} />}
                        </Col>
                    </Appear>

                    <Appear delay={120} style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                        <Col style={{ flex: 1, minWidth: 0 }} gap={18}>
                            <DirectionCard profile={profile} wide />

                            {shown.length > 0 && (
                                <Card style={{ padding: '22px 24px' }}>
                                    {patternsHead}
                                    <Row style={{ gap: 13, marginTop: 16, alignItems: 'stretch' }}>
                                        {shown.map((p, i) => (
                                            <div key={p.code} style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                                                <PatternCard pattern={p} lead={i === 0} segments={8} />
                                            </div>
                                        ))}
                                    </Row>
                                </Card>
                            )}

                            {blocker && (
                                <Row style={{ gap: 18, alignItems: 'stretch' }}>
                                    {blocker.shows.length > 0 && (
                                        <Card style={{ flex: 1, minWidth: 0, padding: '22px 24px' }}>
                                            <Costs items={blocker.shows} hex={(bz as { hex: string }).hex} wide />
                                        </Card>
                                    )}
                                    {blocker.res.length > 0 && (
                                        <Card style={{ flex: 1, minWidth: 0, padding: '22px 24px' }}>
                                            <Results items={blocker.res} wide />
                                        </Card>
                                    )}
                                </Row>
                            )}
                        </Col>
                    </Appear>
                </Row>
                <div style={{ marginTop: 34 }}>
                    <Cta label={t('pw.continue')} onClick={onNext} width={320} height={54} />
                </div>
            </Screen>
        );
    }

    return (
        <Screen column>
            <TopBar onBack={onBack} />
            <Appear>
                <div style={{ marginTop: 16 }}>
                    <Orb score={index} size={196} label={t('pw.pf.index')} />
                </div>
                <div style={{ margin: '20px 20px 0' }}>
                    <ZoneScale score={index} />
                </div>
                <div style={{ padding: '22px 20px 0' }}>{stateLine}</div>
            </Appear>

            {blocker && (
                <div style={{ margin: '16px 20px 0' }}>
                    <Insight blockerName={blocker.name} />
                </div>
            )}

            <div style={{ margin: '12px 20px 0' }}>
                <DirectionCard profile={profile} />
            </div>

            {shown.length > 0 && (
                <div style={{ margin: '18px 0 0' }}>
                    <div style={{ padding: '0 20px 12px' }}>{patternsHead}</div>
                    <Carousel card={262}>
                        {shown.map((p, i) => (
                            <PatternCard key={p.code} pattern={p} lead={i === 0} />
                        ))}
                    </Carousel>
                </div>
            )}

            {blocker && blocker.shows.length > 0 && (
                <div style={{ margin: '18px 20px 0' }}>
                    <Card>
                        <Costs items={blocker.shows} hex={(bz as { hex: string }).hex} />
                    </Card>
                </div>
            )}

            {blocker && blocker.res.length > 0 && (
                <div style={{ margin: '12px 20px 0' }}>
                    <Card>
                        <Results items={blocker.res} />
                    </Card>
                </div>
            )}

            <StickyActions>
                <Cta label={t('pw.continue')} onClick={onNext} />
            </StickyActions>
        </Screen>
    );
};

export default Profile;
